import { LodgeApiError } from "../api/client";
import type { OrganisationMention } from "../api/hooks";
import {
  useDetachOrganisationMention,
  useOrganisationMentions,
} from "../api/hooks";

import styles from "./-OrganisationMentions.module.scss";

// Only http(s) becomes a link. The url is feed-controlled data stored verbatim
// by the ingest, so an empty one would link back to Lodge and a javascript: one
// would execute in Lodge's own origin.
function isSafeArticleUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

function publishedLabel(publishedAt: string): string {
  return new Date(publishedAt).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function MentionRow({
  recordId,
  mention,
}: {
  recordId: string;
  mention: OrganisationMention;
}) {
  const detach = useDetachOrganisationMention();

  return (
    <li className={styles.mention}>
      <div className={styles.title}>
        {isSafeArticleUrl(mention.url) ? (
          <a href={mention.url} target="_blank" rel="noreferrer">
            {mention.title}
          </a>
        ) : (
          mention.title
        )}
      </div>
      {/* The headline often does not name the organisation, so judging "is
          this about them?" from it alone means opening the link. The summary is
          already on the wire. */}
      {mention.summary !== "" && (
        <div className={styles.summary}>{mention.summary}</div>
      )}

      <div className={styles.meta}>
        {mention.sourceName} · {publishedLabel(mention.publishedAt)}
        {/* Who claims it. A person's reading and a matcher's match are not the
            same evidence, and a reader who cannot tell them apart has to trust
            both equally. */}
        {mention.attributedTo === "scout" && (
          <span className={styles.scout}>found by the scout</span>
        )}
      </div>
      <button
        type="button"
        className={styles.remove}
        disabled={detach.isPending}
        onClick={() => detach.mutate({ recordId, newsItemId: mention.id })}
        aria-label={`Remove the mention of ${mention.title}`}
      >
        Not about them
      </button>

      {/* Success is visible - the row goes. Failure was not: the row stayed,
          the button came back, and nothing told the operator whether the click
          had even registered. */}
      {detach.isError && (
        <div className={styles.error} role="alert">
          {detach.error instanceof LodgeApiError && detach.error.detail !== ""
            ? detach.error.detail
            : "That could not be removed. Try again."}
        </div>
      )}
    </li>
  );
}

export function OrganisationMentions({ recordId }: { recordId: string }) {
  const { data, isPending, isError } = useOrganisationMentions(recordId);

  return (
    <section className={styles.section}>
      <h3 className={styles.heading}>
        In the news
        {data !== undefined && data.total > 0 && (
          <span className={styles.count}>{data.total}</span>
        )}
      </h3>

      {isPending && (
        <div className={styles.state} role="status">
          Looking for mentions…
        </div>
      )}

      {isError && (
        <div className={styles.state} role="alert">
          The mentions could not be loaded.
        </div>
      )}

      {data?.items.length === 0 && (
        <div className={styles.state} role="status">
          Nothing in the feed names them yet.
        </div>
      )}

      <ul className={styles.mentions}>
        {data?.items.map((mention) => (
          <MentionRow key={mention.id} recordId={recordId} mention={mention} />
        ))}
      </ul>

      {data !== undefined && data.total > data.items.length && (
        <div className={styles.state}>
          {/* The list is one page of the history. Saying so beats letting a
              partial list read as the whole of it. */}
          Showing {data.items.length} of {data.total}.
        </div>
      )}
    </section>
  );
}
